<template>
  <TransitionExpand axis="x">
    <div
      v-if="isOpen"
      ref="wrapperElement"
      class="fixed right-0 top-1/2 w-170 -translate-y-1/3 z-[52] bg-white shadow-lg h-fitrounded-l-xl border-l border-mid-neutral-100"
    >
      <div
        class="bg-light-neutral-300 text-mid-neutral-500 justify-between border-b border-r border-mid-neutral-50 flex p-3.5 items-center font-medium"
      >
        Screening details
        <button @click="closeDrawer">
          <XMarkIcon class="w-5" />
        </button>
      </div>

      <div class="p-4 space-y-4">
        <div class="h-full w-full">
          <PieChart
            :selected="selectedItem"
            :items="chartData"
            @update:selected="applyFilter"
          />
        </div>

        <div class="flex gap-2 flex-nowrap items-center">
          <button
            v-for="item in chartData"
            :key="item.id"
            class="flex items-center text-xs gap-1 pl-2 pr-3 py-1.5 rounded-full leading-none"
            :class="[
              item.badgeClasses,
              { 'ring-blue-400 ring': selectedItem === item.id },
            ]"
            @click="applyFilter(item.id)"
          >
            <div class="flex items-center gap-1">
              <div class="rounded-full bg-white/40 p-1.5">
                {{ item.value }}
              </div>
              {{ item.label }}
            </div>
          </button>

          <button
            class="flex items-center text-xs gap-1 pl-2 pr-3 py-1.5 rounded-full leading-none bg-blue-200 text-blue-600"
            :class="[{ 'ring-blue-400 ring': selectedItem === null }]"
            @click="clearSelection()"
          >
            <div class="flex items-center gap-1">
              <div class="rounded-full bg-white/40 p-1.5">
                {{ review.entity?.value?.stats?.overall?.total ?? 0 }}
              </div>
              All
            </div>
          </button>
        </div>
      </div>
    </div>
  </TransitionExpand>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import PieChart from '@app/components/PieChart.vue'
import { injectStrict } from '@app/utils/injectStrict'

import { StudyState } from '@app/types'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ReviewKey } from '../../use-review'
import { TransitionExpand } from '@morev/vue-transitions'
import { onClickOutside } from '@vueuse/core'

const review = injectStrict(ReviewKey)

const isOpen = defineModel<boolean>({
  default: false,
})

function closeDrawer() {
  isOpen.value = false
}
const wrapperRef = useTemplateRef('wrapperElement')
onClickOutside(wrapperRef, () => {
  isOpen.value = false
})

const selectedItem = computed<StudyState | null>(() => {
  if (!review.filtering.hasFiltersApplied.value) {
    return null
  }
  return Object.keys(review.filtering.filters.value).find(
    (key) => review.filtering.filters.value[key as StudyState],
  ) as StudyState
})

const chartData = computed(() => [
  {
    label: 'Included',
    value: review.entity.value.stats.overall.included ?? 0,
    color: '#4ade80',
    calloutColor: '#16a34a',
    badgeClasses: 'bg-green-200 text-green-800',
    id: StudyState.INCLUDED,

    order: 1,
  },
  {
    label: 'Excluded',
    value:
      (review.entity.value.stats.overall
        .titleAndAbstractExcludedScreeningTotal ?? 0) +
      (review.entity.value.stats.overall.fullTextExcludedScreeningTotal ?? 0),
    color: '#f87171',
    calloutColor: '#dc2626',
    badgeClasses: 'bg-red-200 text-red-800',
    id: StudyState.EXCLUDED,
    order: 2,
  },
  {
    label: 'Duplicates',
    value: review.entity.value.stats.overall.duplicate ?? 0,
    color: '#ffd6a8',
    calloutColor: '#F97316',
    badgeClasses: 'bg-orange-200 text-orange-800',
    id: StudyState.DUPLICATE,
    order: 3,
  },
  {
    label: 'Quarantined',
    value: review.entity.value.stats.overall.quarantined ?? 0,
    color: '#fff085',
    badgeClasses: 'bg-yellow-200 text-yellow-800',
    calloutColor: '#ca8a04',
    id: StudyState.QUARANTINED,
    order: 4,
  },
  {
    label: 'Unscreened',
    value: review.entity.value.stats.overall.unscreened ?? 0,
    color: '#e5e7eb',
    calloutColor: '#475569',
    badgeClasses: 'bg-slate-200 text-slate-800',
    id: StudyState.UNSCREENED,
    order: 5,
  },
])

function applyFilter(id: StudyState | null | undefined) {
  if (!id || selectedItem.value === id) {
    clearSelection()
  } else {
    setFilter(id)
  }
}

function clearSelection() {
  review.filtering.resetFilters()
}

function setFilter(filter: StudyState) {
  review.filtering.filterStudiesByState(filter)
}
</script>
